import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage, useIntl } from 'dibs-react-intl';
import { getMeasurementUnit } from 'dibs-intl/exports/units';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { Badge } from 'dibs-elements/exports/Badge.tsx';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { PdpSharedSimilarSoldItemsDetailsCell } from './PdpSharedSimilarSoldItemsDetailsCell.tsx';
import { getDetailLabelAndValue } from './getDetailLabelAndValue.ts';
import { getSrcsetUrl } from 'dibs-image-utils/exports/srcSet.ts';
import { FormattedPrice } from 'dibs-buyer-price';
import { resolveSoldDate } from './resolveSoldDate.tsx';
import { useSimilarSoldItemsTracking } from '../hooks/useSimilarSoldItemsTracking.ts';

import styles from './PdpSharedSimilarSoldItemsColumn.scss';
import classnames from 'classnames';
import dibsCss from 'dibs-css';

import { type PdpSharedSimilarSoldItemsColumn_viewer$key } from './__generated__/PdpSharedSimilarSoldItemsColumn_viewer.graphql.ts';
import { type PdpSharedSimilarSoldItemsColumn_item$key } from './__generated__/PdpSharedSimilarSoldItemsColumn_item.graphql.ts';
import { type PdpSharedSimilarSoldItemsColumn_user$key } from './__generated__/PdpSharedSimilarSoldItemsColumn_user.graphql.ts';

const viewerFragment = graphql`
    fragment PdpSharedSimilarSoldItemsColumn_viewer on Viewer {
        ...useSimilarSoldItemsTracking_viewer
    }
`;

const itemFragment = graphql`
    fragment PdpSharedSimilarSoldItemsColumn_item on Item
    @argumentDefinitions(
        userId: { type: "String", defaultValue: "" }
        isTrade: { type: "Boolean", defaultValue: false }
        userCountryCode: { type: "String" }
        priceBookName: { type: "String" }
        catchNonFatalErrors: { type: "Boolean", defaultValue: false }
    ) {
        serviceId
        title
        vertical
        photoUrl: firstPhotoWebPath(size: master)
        localizedPdpUrl
        displayPrice(
            isTrade: $isTrade
            buyerId: $userId
            userCountryCode: $userCountryCode
            priceBookName: $priceBookName
            catchNonFatalErrors: $catchNonFatalErrors
            canShowOnHoldForDifferentBuyer: false
        ) {
            convertedAmountList {
                amount
                currency
            }
        }
        ...getDetailLabelAndValue_item
    }
`;

const userFragment = graphql`
    fragment PdpSharedSimilarSoldItemsColumn_user on User {
        preferences {
            measurementUnit
        }
    }
`;

const ItemImageAndTitle: FC<{ imageUrl: string; title: string }> = ({ imageUrl, title }) => (
    <div className={dibsCss.pxXsmall}>
        <div
            className={classnames(
                styles.wrapper,
                styles.itemImageDimensions,
                dibsCss.roundedXxsmall,
                dibsCss.mtXsmall,
                dibsCss.mxAuto
            )}
        >
            <img
                className={classnames(
                    styles.itemImage,
                    styles.itemImageDimensions,
                    dibsCss.objectCover
                )}
                src={getSrcsetUrl(imageUrl, 200)}
                alt={title}
            />
        </div>
        <div
            className={classnames(
                dibsCss.sassyFontBodySmall,
                dibsCss.textSatan,
                dibsCss.mtXsmall,
                dibsCss.lineClamp2
            )}
        >
            {title}
        </div>
    </div>
);

export const PdpSharedSimilarSoldItemsColumn: FC<{
    viewer: PdpSharedSimilarSoldItemsColumn_viewer$key;
    item?: PdpSharedSimilarSoldItemsColumn_item$key | null;
    user?: PdpSharedSimilarSoldItemsColumn_user$key | null;
    itemSoldDate?: string | null;
    currency: string;
    isCurrentItem?: boolean;
    isLastItem?: boolean;
    isBelowEstimate?: boolean;
    inModal?: boolean;
    detailEntries: string[];
}> = ({
    viewer: viewerRef,
    item: itemRef,
    user: userRef,
    itemSoldDate,
    currency,
    isCurrentItem,
    isLastItem,
    isBelowEstimate,
    detailEntries,
    inModal,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const { isMobile } = useServerVarsContext();
    const { locale, formatMessage } = useIntl();
    const { trackSimilarSoldItemsEcommerceClick } = useSimilarSoldItemsTracking({ viewer });
    const itemId = item?.serviceId;

    const priceAmount = item?.displayPrice?.[0]?.convertedAmountList?.find(
        convertedAmount => convertedAmount && convertedAmount.currency === currency
    )?.amount;

    const measurementUnit = user ? user.preferences?.measurementUnit : getMeasurementUnit(locale);

    const { photoUrl, title, localizedPdpUrl, vertical } = item || {};

    if (!photoUrl || !title || !localizedPdpUrl || !measurementUnit || !vertical || !itemId) {
        return null;
    }

    return (
        <div
            className={classnames(dibsCss.relative, dibsCss.rounded2px, {
                [styles.similarSoldItemColumn]: !isCurrentItem,
                [styles.similarSoldCurrentItemColumn]: isCurrentItem,
                [dibsCss.pbXsmall]: !isCurrentItem,
                [dibsCss.bgWhite]: !inModal && isCurrentItem,
                [dibsCss.bgMoonstone]: inModal && isCurrentItem,
            })}
        >
            {isCurrentItem ? (
                <ItemImageAndTitle imageUrl={photoUrl} title={title} />
            ) : (
                <Link
                    href={localizedPdpUrl}
                    target="_blank"
                    underline="none"
                    title={title}
                    onClick={() => {
                        trackSimilarSoldItemsEcommerceClick({ itemId });
                    }}
                >
                    <ItemImageAndTitle imageUrl={photoUrl} title={title} />
                </Link>
            )}
            {isCurrentItem && (
                <div
                    className={classnames(dibsCss.absolute, {
                        [dibsCss.topXsmall]: inModal || !isMobile,
                        [dibsCss.rightXsmall]: inModal || !isMobile,
                        [dibsCss.topSmall]: !inModal && isMobile,
                        [dibsCss.rightSmall]: !inModal && isMobile,
                    })}
                >
                    <Badge type="pill" size="small-regular" backgroundColor="noir">
                        <FormattedMessage
                            id="abf.PdpRespSimilarSoldItemsColumn.currentItem"
                            defaultMessage="Current Item"
                        />
                    </Badge>
                </div>
            )}
            <PdpSharedSimilarSoldItemsDetailsCell
                entry="PRICE"
                label={
                    isCurrentItem ? (
                        <FormattedMessage
                            id="abf.PdpRespSimilarSoldItemsColumn.currentPrice"
                            defaultMessage="Current Price"
                        />
                    ) : (
                        <FormattedMessage
                            id="abf.PdpRespSimilarSoldItemsColumn.soldPrice"
                            defaultMessage="Sold Price"
                        />
                    )
                }
                value={<FormattedPrice value={priceAmount?.toString() || ''} currency={currency} />}
                isCurrentItem={isCurrentItem}
                isLastItem={isLastItem}
                isBelowEstimate={isBelowEstimate}
            />
            <PdpSharedSimilarSoldItemsDetailsCell
                label={
                    <FormattedMessage
                        id="abf.PdpRespSimilarSoldItemsColumn.soldDate"
                        defaultMessage="Sold Date"
                    />
                }
                value={resolveSoldDate({ itemSoldDate, isCurrentItem })}
                isLastItem={isLastItem}
            />
            {detailEntries.map((entry, idx) => {
                const { label, value } = getDetailLabelAndValue({
                    item,
                    entry,
                    measurementUnit,
                    formatMessage,
                });
                return (
                    <PdpSharedSimilarSoldItemsDetailsCell
                        key={`detail-${idx}`}
                        label={label}
                        value={value}
                        isCurrentItem={isCurrentItem}
                        isLastItem={isLastItem}
                        entry={entry}
                    />
                );
            })}
        </div>
    );
};
